import { Link } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateKnowledgeGraphSchema,
  generateSpeakableSchema,
  type SiteInfoSettings,
} from "@/lib/seo-utils";
import { useQuery } from "@tanstack/react-query";
import { Shield, Heart, MapPin, Star } from "lucide-react";
import { wavesVideo, parallaxBg, beachShoreline, portfolioShoreline, wavesPoster, portfolioHeroVideo, portfolioHeroPoster } from "@/lib/media-assets";
import { LazyVideo } from "@/components/ui/lazy-video";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { useFeatureFlags } from "@/hooks/use-feature-flags";

const HOST = {
  name: "Vacation Rental Oahu Host Team",
  jobTitle: "Property Host",
  bio: "Local hosts dedicated to providing an exceptional direct-book beachfront experience in Ka'a'awa. We combine hospitality expertise with deep knowledge of Oahu's windward coast to help guests discover the best of island living.",
};

export default function AboutPage() {
  const bookCta = useBookCta();
  const flags = useFeatureFlags();
  const showPortfolioCopy = flags.portfolioHomepage && flags.publishedHomeCount > 1;
  const { seoSettings } = useSEOSettings();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ["/api/settings/site_info"],
    retry: false,
  });

  const seoHead = useSEO({
    pageType: "about",
    canonical: "https://vacationrentaloahu.co/about",
    structuredData: [
      generateKnowledgeGraphSchema({
        seoSettings,
        siteInfoSettings: siteSettings,
        host: HOST,
      }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About", url: "/about" },
      ]),
      generateSpeakableSchema(["h1", "h2"]),
    ],
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow="Our Story"
            title={showPortfolioCopy ? "About Vacation Rental Oahu" : "About The Kualoa Beach House"}
            subtitle={showPortfolioCopy
              ? "Vacation Rental Oahu is a small collection of beachfront homes on Oahu's windward coast. Every guest books direct and gets personal support from the host team."
              : "We operate a premium beachfront vacation rental in Ka'a'awa, not a property management portfolio. Every guest books direct and gets personal support."}
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt={showPortfolioCopy
              ? "Ka'a'awa shoreline looking toward Mokoli'i and the Kualoa mountains"
              : "Beachfront ocean views at The Kualoa Beach House in Ka'a'awa, Oahu"}
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-4xl">
              <Breadcrumbs
                items={[
                  { name: "Home", url: "/" },
                  { name: "About", url: "/about" },
                ]}
                className="mb-16 justify-center"
              />

              <ScrollReveal>
                <div className="mb-20 flex flex-col md:flex-row gap-10 md:gap-16 items-start">
                  <div className="w-12 h-12 rounded-full bg-sand border border-sand-deep flex items-center justify-center shrink-0 mt-1">
                    <Heart className="h-5 w-5 text-ink" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <div>
                    <SectionHeading
                      eyebrow="The Team"
                      title="Meet your hosts"
                      align="left"
                      className="mb-6"
                    />
                    <p className="text-luxury-lg text-ink-soft leading-relaxed mb-6">{HOST.bio}</p>
                    <p className="text-ink-soft leading-relaxed">
                      Read what guests say on our{" "}
                      <Link href="/reviews" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">guest reviews page</Link>
                      , real feedback from families who have stayed at our Ka&apos;a&apos;awa beach house.
                    </p>
                  </div>
                </div>
              </ScrollReveal>

              <div className="h-px bg-sand-deep w-full mb-20" />

              <SectionHeading
                eyebrow="Our Philosophy"
                title="Guest-first hospitality"
                lede="From pre-arrival guides to local recommendations, we help you experience Oahu like an insider, not a tourist checklist."
                className="mb-16"
              />

              <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-4">
                <ScrollReveal>
                  <div className="h-full bg-sand border border-sand-deep p-8 md:p-10">
                    <MapPin className="h-7 w-7 text-ink mb-5" aria-hidden="true" />
                    <h3 className="text-luxury-xl font-serif text-forest mb-4">Ka&apos;a&apos;awa local expertise</h3>
                    <p className="text-ink-soft leading-relaxed">
                      Our property sits on the windward coast across from Kualoa Ranch. We publish the{" "}
                      <Link href="/blog" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">Oahu Travel Guide</Link> to
                      share beaches, hikes, and culture beyond the guidebooks.
                    </p>
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={0.08}>
                  <div className="h-full bg-sand border border-sand-deep p-8 md:p-10">
                    <Shield className="h-7 w-7 text-ink mb-5" aria-hidden="true" />
                    <h3 className="text-luxury-xl font-serif text-forest mb-4">Direct booking trust</h3>
                    <p className="text-ink-soft leading-relaxed">
                      Book securely on our site with transparent pricing. Read our{" "}
                      <Link href="/policies" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">policies</Link>,{" "}
                      the <Link href="/house-rules" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">house rules</Link> and{" "}
                      every <Link href="/reviews" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">guest review</Link> before you reserve.
                      {siteSettings?.license_number && (
                        <span className="block mt-2 text-sm text-driftwood">License: {siteSettings.license_number}</span>
                      )}
                    </p>
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={0.12} className="md:col-span-2">
                  <div className="bg-sand border border-sand-deep p-8 md:p-10 text-center">
                    <Star className="h-7 w-7 text-sun mx-auto mb-5" aria-hidden="true" fill="currentColor" />
                    <h3 className="text-luxury-xl font-serif text-forest mb-4">Verified guest reviews</h3>
                    <p className="text-ink-soft leading-relaxed max-w-lg mx-auto">
                      Every review on the site was written by a guest who stayed, and is published with its wording and rating unchanged. Read them all on the{" "}
                      <Link href="/reviews" className="text-ink border-b border-sand-deep hover:border-sun transition-colors">guest reviews page</Link>.
                    </p>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>

          <section className="scenic-editorial relative overflow-hidden" aria-labelledby="book-direct-heading">
            <div className="absolute inset-0 z-0" aria-hidden="true">
              <LazyVideo
                src={portfolioHeroVideo}
                poster={portfolioHeroPoster}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />

            </div>
            <div className="relative z-10 container mx-auto px-6 max-w-2xl py-20 md:py-28 text-center media-copy-panel">
              <p className="wordmark-gold text-[11px] font-sans uppercase tracking-[0.3em] mb-3">Book Direct</p>
              <h2
                id="book-direct-heading"
                className="wordmark-gold font-serif text-[clamp(28px,5vw,44px)] leading-tight tracking-tight mb-5"
              >
                Why book direct?
              </h2>
              <p className="text-ink-soft leading-relaxed mb-10 font-sans">
                Direct booking means better communication, no platform markup, and hosts who genuinely care about your stay. Compare our amenities and location, then reserve your dates in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href={bookCta.href} className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-transform duration-300 hover:-translate-y-0.5">
                  {bookCta.choosing ? "Choose a Home" : "Book Your Stay"}
                </Link>
                <Link href="/faq" className="inline-flex items-center justify-center border border-[#E6D2A0]/60 text-[#E6D2A0] hover:bg-ink/30 font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-all duration-300">
                  Read FAQ
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
